import { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import logo from "../assets/fullLogo2.PNG";

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeDown = keyframes`
  from { opacity: 0; transform: translateY(-14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 clamp(1.5rem, 4vw, 3.5rem);
  height: 68px;
  animation: ${fadeDown} 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;

  /* Glass effect — gets slightly more opaque on scroll (handled via JS class) */
  background: rgba(10, 0, 16, 0);
  backdrop-filter: blur(0px);
  border-bottom: 1px solid transparent;
  background: rgba(10, 0, 16, 0.75);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border-color: rgba(168, 85, 253, 0.15);

  transition:
    background 0.35s ease,
    backdrop-filter 0.35s ease,
    border-color 0.35s ease;

  &.scrolled {
    background: rgba(10, 0, 16, 0.75);
    backdrop-filter: blur(20px) saturate(160%);
    -webkit-backdrop-filter: blur(20px) saturate(160%);
    border-color: rgba(168, 85, 253, 0.15);
  }
`;

// ── Logo
const LogoWrap = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;

  img {
    height: 32px;
    width: auto;
    object-fit: contain;
    display: block;
  }
`;

// ── Menu
const MenuList = styled.ul`
  display: flex;
  align-items: center;
  gap: clamp(1.5rem, 3vw, 2.8rem);
  list-style: none;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 767px) {
    display: none;
  }
`;

const MenuItem = styled.li``;

const MenuLink = styled.a`
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 400;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  text-decoration: none;
  position: relative;
  transition: color 0.2s ease;

  &::after {
    content: "";
    position: absolute;
    bottom: -3px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, #a855f7, #e879f9);
    transform: scaleX(0);
    transform-origin: center;
    transition: transform 0.25s cubic-bezier(0.23, 1, 0.32, 1);
    border-radius: 1px;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.95);
    &::after {
      transform: scaleX(1);
    }
  }
`;

// ── App Store button
const AppStoreBtn = styled.a`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 18px;
  height: 40px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background: rgba(168, 85, 253, 0.15);
    border-color: rgba(168, 85, 253, 0.5);
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(168, 85, 253, 0.25);
  }

  &:active {
    transform: scale(0.97);
  }
`;

const AppleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
    fill: rgba(255, 255, 255, 0.85);
  }
`;

const AppStoreCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const AppStoreTop = styled.span`
  font-family: "DM Sans", sans-serif;
  font-size: 0.55rem;
  font-weight: 400;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  line-height: 1;
`;

const AppStoreBottom = styled.span`
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.2;
  letter-spacing: 0.01em;
`;

// ─── Nav items ────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { label: "How it Works", href: "#how-it-works" },
  { label: "Features", href: "#features" },
  { label: "Safety", href: "#safety" },
  { label: "About", href: "#about" },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Nav className={scrolled ? "scrolled" : ""}>
      {/* ── Logo */}
      <LogoWrap href="/">
        <img src={logo} alt="Logo" />
      </LogoWrap>

      {/* ── Menu (hidden on mobile) */}
      <MenuList>
        {NAV_LINKS.map(({ label, href }) => (
          <MenuItem key={href}>
            <MenuLink href={href}>{label}</MenuLink>
          </MenuItem>
        ))}
      </MenuList>

      {/* ── App Store CTA */}
      <AppStoreBtn
        href="https://apps.apple.com"
        target="_blank"
        rel="noopener noreferrer"
      >
        <AppleIcon>
          {/* Apple logo SVG */}
          <svg viewBox="0 0 814 1000" xmlns="http://www.w3.org/2000/svg">
            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46.7 790.7 0 663 0 541.8c0-207.5 135.4-317.3 268.9-317.3 70.2 0 128.6 46.4 173 46.4 42.4 0 109.1-49.1 186.3-49.1 30.4 0 110.2 3.9 162.7 69.2zm-85.5-220.5c39.5-46.4 67.8-110.9 67.8-175.4 0-8.9-.6-17.8-2.2-25.4-64.4 2.6-140.5 43.7-185.5 93.8-35.8 39.5-69.7 103.4-69.7 168.5 0 9.5 1.6 19 2.2 22.3 3.9.6 10.4 1.6 16.9 1.6 57.7 0 129.4-39.5 170.5-85.4z" />
          </svg>
        </AppleIcon>
        <AppStoreCopy>
          <AppStoreTop>Download on the</AppStoreTop>
          <AppStoreBottom>App Store</AppStoreBottom>
        </AppStoreCopy>
      </AppStoreBtn>
    </Nav>
  );
}
